/**
 * 業務ルールの中身を描く部品 — 参照 1 つの表示名、判定表のセル、欄 1 行、デシジョンテーブル、式の行の文。
 * 業務ルールの区画（`rule-detail-sections.tsx`）と並べ替え（`rule-sort-body.tsx`）が同じものを使う。
 *
 * **式は行の文で出す**（設計書 api §6 の中身の見せ方）。段ごとに「名前 ＝ 項」を 1 行目に、続く項を
 * 演算子から始まる行に置き、条件つきの項には「（条件のプロパティが 値 とき）」を添える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの中身の参照・判定表・式を、区画と並べ替えが共有する部品として描く。
 * @graph-connects displayLabel [calls] 表示名の穴埋め
 */
import type {
  DecisionTableCellView,
  ElementRef,
  FormulaOperandView,
  RuleBodyView,
  RuleView,
} from '@cortex/domain-spec-console';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { DiffSide } from '../components/spec/DiffSplitPanes.js';
import { ElementTermLink } from '../components/spec/TermText.js';
import { displayLabel, SpecLabelBadges } from '../components/spec/UntranslatedBadge.js';
import { InfoMark } from './screen-detail-cells.js';

/** 値のセル。用語が語の途中で折れると読めないので折り返さない。 @graph-connects none */
export const VALUE_CELL_CLASS =
  'whitespace-nowrap px-2 py-1 align-top text-[var(--color-text-primary)]';

/** 見出しのセル。 @graph-connects none */
export const HEAD_CELL_CLASS =
  'whitespace-nowrap px-2 pb-1 text-left align-bottom font-normal text-[var(--color-text-muted)]';

/** 判定表の出力の列。条件の列との境に線を引く。 @graph-connects none */
const OUTPUT_EDGE_CLASS = 'border-l-2 border-[var(--color-border)]';

/** 見ない条件・空の値。 @graph-connects none */
export const MUTED_CLASS = 'text-[var(--color-text-muted)]';

/**
 * 突き合わせで見比べる姿。案件を選んでいなければ省く。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの区画が突き合わせに使う面と反対側の姿
 * @graph-connects none
 */
export interface RuleCompareProps {
  /** 描いている面。 */
  side?: DiffSide;
  /** 反対側の姿。反対側に無ければ null。 */
  counterpart?: RuleView | null;
}

/**
 * 反対側に同じ番号が無いか。案件を選んでいなければ常に false。
 *
 * @param ids 反対側の番号。案件を選んでいなければ undefined
 * @param id 見る番号
 * @returns 反対側に無ければ true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 突き合わせで、ある番号が反対側の姿に無いかを判定する。
 * @graph-connects none
 */
export function isMissingOn(ids: readonly number[] | undefined, id: number): boolean {
  return ids !== undefined && !ids.includes(id);
}

/** `ElementRefText` の props。 @graph-connects none */
interface ElementRefTextProps {
  /** 出す参照。無ければ「—」を出す */
  value: ElementRef | null | undefined;
}

/**
 * 要素への参照 1 つを表示名で出す。番号は `data-id` にだけ残す。参照が用語なら表示名を
 * 用語詳細へのリンクにする（業務の入力・業務ルールの出力・判定表のセルなど、参照を描く所すべて）。
 *
 * `<a>` ・ `<button>` の中では使わない。その場所では `displayLabel` で表示名だけを描く。
 *
 * @param props 出す参照
 * @returns 表示名（用語なら用語詳細へのリンク）と未翻訳の印
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 要素への参照 1 つを表示名と未翻訳の印で描き、用語なら用語詳細へ繋ぎ、番号は出さない。
 * @graph-connects displayLabel [calls] 表示名の穴埋め
 * @graph-connects ElementTermLink [calls] 用語なら用語詳細へのリンク
 */
export function ElementRefText({ value }: ElementRefTextProps) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <span className={MUTED_CLASS}>—</span>;
  const label = displayLabel(value.label, t, value.untranslated);
  return (
    <span className="inline-flex items-center gap-1" data-id={value.id}>
      <ElementTermLink id={value.id} title={label}>
        {label}
      </ElementTermLink>
      <SpecLabelBadges label={value.label} untranslated={value.untranslated} />
    </span>
  );
}

/**
 * 見出しの名前に添える「未翻訳」を、札ではなく指すと出す文にする。名前は読めるが選んだ言語の訳が
 * 欠けているときだけ返す（名前が空なら本文がすでに「未翻訳」と言っている）。
 *
 * @param label 表示名
 * @param untranslated 選んだ言語に訳が入っていないか
 * @param t 表示言語への翻訳
 * @returns title に入れる文。添えないなら undefined
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール・業務ロジックの見出しの名前に、未翻訳の断りを指すと出す文として添える。
 * @graph-connects none
 */
export function untranslatedTitle(
  label: string | null,
  untranslated: boolean,
  t: (key: string) => string,
): string | undefined {
  return untranslated && (label ?? '') !== '' ? t('spec.untranslated') : undefined;
}

/** `DecisionTableCellText` の props。 @graph-connects none */
interface DecisionTableCellTextProps {
  /** 判定表のセル 1 つ（値・「—」・範囲）。 */
  value: DecisionTableCellView;
}

/**
 * 判定表のセル 1 つを描く。値と「—」は `ElementRefText`、範囲は比べ方と相手を並べる。
 *
 * @param props セル
 * @returns セルの中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表のセル 1 つ（値・見ない・範囲）を描く
 * @graph-connects ElementRefText [calls] 値のセルと相手の用語の表示
 */
export function DecisionTableCellText({ value }: DecisionTableCellTextProps) {
  const { t } = useTranslation();
  if (value === null || !('comparator' in value)) return <ElementRefText value={value} />;
  return (
    <span className="inline-flex items-center gap-1" data-range={value.comparator}>
      {t(`businessLogic.rangeComparators.${value.comparator}`)}
      {typeof value.operand === 'number' ? (
        <span>{value.operand}</span>
      ) : (
        <ElementRefText value={value.operand} />
      )}
    </span>
  );
}

/** `RuleFieldRow` の props。 @graph-connects none */
interface RuleFieldRowProps {
  /** 欄の名前。 */
  label: string;
  /** 欄の値。 */
  children: ReactNode;
}

/**
 * 欄 1 行（名前と値）。
 *
 * @param props 欄の名前と値
 * @returns 欄 1 行
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 欄 1 行を名前と値で描く。
 * @graph-connects none
 */
export function RuleFieldRow({ label, children }: RuleFieldRowProps) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-sm">
      <span className={MUTED_CLASS}>{label}</span>
      <span className="min-w-0 text-[var(--color-text-primary)]">{children}</span>
    </div>
  );
}

/** `DecisionTableBody` の props。 @graph-connects none */
interface DecisionTableBodyProps {
  /** 判定表の中身。 */
  body: Extract<RuleBodyView, { kind: 'decisionTable' }>;
  /** 行に敷く地色（案件で足した・外したキーの決め方）。無ければ空。 */
  rowClass?: string;
  /**
   * ヒットの決まりと「—」の凡例の出し方。`info` は「#」の列の見出しの ⓘ（指すと出る）、`line` は表の上の
   * 1 行。中身の表は ⓘ にし、畳みの中のキーの決め方は今の 1 行のまま。既定は `line`。
   */
  note?: 'info' | 'line';
}

/**
 * 判定表の読み方の断り — ヒットの決まりと「—」の凡例を 1 本の文にする。
 *
 * @param overlapPolicy ヒットの決まり
 * @param t 表示言語への翻訳
 * @returns 「ヒットの決まり: … ／ 「—」＝ …」
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表のヒットの決まりと「—」の凡例を、ⓘ に入れる 1 本の文にする。
 * @graph-connects none
 */
function decisionTableNote(
  overlapPolicy: Extract<RuleBodyView, { kind: 'decisionTable' }>['overlapPolicy'],
  t: (key: string) => string,
): string {
  return `${t('businessLogic.hitPolicy')}: ${t(`businessLogic.hitPolicies.${overlapPolicy}`)} ／ ${t('businessLogic.notLooked')}`;
}

/**
 * 判定表をデシジョンテーブルで出す。1 列目に行番号、条件の列 → 出力の列。ヒットの決まりと「—」の
 * 凡例は、中身の表では「#」の見出しの ⓘ に畳み（表の読み方の断りで、問いの答えではない。Dhik 2026-09-29
 * 「情報を絞る」）、キーの決め方では表の上の 1 行に出す。
 *
 * @param props 判定表の中身・行の地色・断りの出し方
 * @returns デシジョンテーブル
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 判定表を、ヒットの決まりと「—」の凡例を ⓘ か 1 行で添えたデシジョンテーブルとして描く。
 * @graph-connects DecisionTableCellText [calls] 条件のセル
 * @graph-connects InfoMark [calls] 読み方の断りの ⓘ
 */
export function DecisionTableBody({ body, rowClass = '', note = 'line' }: DecisionTableBodyProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {note === 'line' && (
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-text-secondary)]">
          <span data-hit-policy={body.overlapPolicy}>
            {t('businessLogic.hitPolicy')}:{' '}
            <span className="font-bold text-[var(--color-text-primary)]">
              {t(`businessLogic.hitPolicies.${body.overlapPolicy}`)}
            </span>
          </span>
          <span>{t('businessLogic.notLooked')}</span>
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs" data-rule-body="decisionTable">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th scope="col" className={HEAD_CELL_CLASS}>
                #
                {note === 'info' && (
                  <span data-hit-policy={body.overlapPolicy}>
                    <InfoMark text={decisionTableNote(body.overlapPolicy, t)} />
                  </span>
                )}
              </th>
              {body.inputs.map((input) => (
                <th key={`in-${String(input.id)}`} scope="col" className={HEAD_CELL_CLASS}>
                  <ElementRefText value={input} />
                </th>
              ))}
              {body.outputs.map((output, index) => (
                <th
                  key={`out-${String(output.id)}`}
                  scope="col"
                  className={`${HEAD_CELL_CLASS}${index === 0 ? ` ${OUTPUT_EDGE_CLASS}` : ''}`}
                >
                  <ElementRefText value={output} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={`border-b border-[var(--color-border)]${rowClass}`}
                data-decision-row={rowIndex + 1}
              >
                <td className={`${VALUE_CELL_CLASS} ${MUTED_CLASS}`}>{rowIndex + 1}</td>
                {row.cells.map((cell, index) => (
                  <td key={`${String(row.id)}-in-${String(index)}`} className={VALUE_CELL_CLASS}>
                    <DecisionTableCellText value={cell} />
                  </td>
                ))}
                {row.outputs.map((output, index) => (
                  <td
                    key={`${String(row.id)}-out-${String(index)}`}
                    className={`${VALUE_CELL_CLASS} font-bold${index === 0 ? ` ${OUTPUT_EDGE_CLASS}` : ''}`}
                  >
                    <ElementRefText value={output} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 式の段。 @graph-connects none */
type FormulaStepView = Extract<RuleBodyView, { kind: 'formula' }>['steps'][number];

/** 文言を引く関数。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * 式の項 1 つを文にする。用語の項に条件があれば「（条件のプロパティが 値 とき）」を添える。
 *
 * @param operand 項
 * @param stepName 段の番号から段の名前を引く
 * @param t 文言
 * @returns 項の文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 式の項 1 つ（用語・上の段・数値）を文にし、条件つきの項には条件を添える。
 * @graph-connects displayLabel [calls] 表示名の穴埋め
 */
function operandTextOf(
  operand: FormulaOperandView,
  stepName: (id: number) => string,
  t: Translate,
): string {
  if (operand.kind === 'step') return stepName(operand.step);
  if (operand.kind === 'literal') {
    const unit =
      operand.unit === null ? '' : displayLabel(operand.unit.label, t, operand.unit.untranslated);
    return `${String(operand.literal)}${unit}`;
  }
  const term = displayLabel(operand.term.label, t, operand.term.untranslated);
  if (operand.when === null) return term;
  return `${term}${t('businessLogic.formulaWhen', {
    property: displayLabel(operand.when.property.label, t, operand.when.property.untranslated),
    value: displayLabel(operand.when.value.label, t, operand.when.value.untranslated),
  })}`;
}

/** `FormulaLines` の props。 @graph-connects none */
interface FormulaLinesProps {
  /** 式の段（上から順）。 */
  steps: readonly FormulaStepView[];
  /** 枠に敷く地色（案件で足した・外したキーの決め方）。無ければ空。 */
  className?: string;
}

/**
 * 式を行の文で出す。段ごとに 1 行目を「名前 ＝ 項」、続く項を演算子から始まる行にする。項に出る段の
 * 番号は段の名前に置き換える。
 *
 * @param props 式の段と枠の地色
 * @returns 行の文の並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 式の段を「名前 ＝ 項」と演算子から始まる行の文にして、条件つきの項に条件を添えて描く。
 * @graph-connects operandTextOf [calls] 項の文
 */
export function FormulaLines({ steps, className = '' }: FormulaLinesProps) {
  const { t } = useTranslation();
  const stepName = (id: number): string => {
    const step = steps.find((entry) => entry.id === id);
    return step === undefined
      ? t('spec.unset')
      : displayLabel(step.name.label, t, step.name.untranslated);
  };

  return (
    <div
      data-rule-body="formula"
      className={`flex flex-col gap-2 bg-[var(--color-bg-elevated)] px-3 py-2 font-mono text-xs leading-6 text-[var(--color-text-primary)]${className}`}
    >
      {steps.map((step) => (
        <div key={step.id} data-formula-step={step.id}>
          {step.operands.map((operand, index) => (
            <p
              key={`${String(step.id)}-${String(index)}`}
              data-formula-line
              className={index === 0 ? '' : 'pl-8'}
            >
              {index === 0
                ? `${stepName(step.id)}${t('businessLogic.formulaEquals')}`
                : `${t(`businessLogic.formulaOperators.${step.operator}`).trim()} `}
              {operandTextOf(operand, stepName, t)}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}
